import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Navigation, Clock } from "lucide-react";
import { trackEvent } from "@/hooks/useBusinessData";

export default function Location({ config }) {
  const today = new Date().getDay(); // 0 = Sunday
  const hoursOrder = [1, 2, 3, 4, 5, 6, 0]; // Mon..Sun order
  const mapEmbed = `https://www.google.com/maps?q=${encodeURIComponent(
    `${config.address_line_1} ${config.address_line_2}`
  )}&z=15&output=embed`;

  return (
    <section
      id="visit"
      className="relative bg-black text-white py-24 md:py-40 border-t border-white/10 overflow-hidden"
      data-testid="location-section"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10 mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-6">
            <div className="eyebrow mb-6">§ Visit She Cuts</div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="font-serif text-4xl sm:text-6xl md:text-7xl leading-[0.95] tracking-tight"
            >
              759 W Peachtree.
              <br />
              <span className="italic text-zinc-400">Midtown Atlanta.</span>
            </motion.h2>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-8">
          {/* Map */}
          <div className="col-span-12 md:col-span-7 aspect-[16/12] md:aspect-auto md:min-h-[560px] border border-white/10 overflow-hidden bg-zinc-900">
            <iframe
              title="She Cuts Barber Studio location"
              src={mapEmbed}
              className="w-full h-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          {/* Info */}
          <div className="col-span-12 md:col-span-5 flex flex-col">
            <div className="border border-white/10 p-6 md:p-8">
              <div className="flex items-start gap-4">
                <MapPin className="w-5 h-5 mt-1 flex-none" strokeWidth={1.4} />
                <div>
                  <div className="eyebrow text-zinc-500 mb-1">Address</div>
                  <div className="font-serif text-xl md:text-2xl leading-tight">
                    {config.address_line_1}
                  </div>
                  <div className="text-zinc-400 mt-1">
                    {config.address_line_2}
                  </div>
                  <div className="text-zinc-500 mt-1 text-sm">
                    {config.neighborhood}
                  </div>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <a
                  href={config.google_maps_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent("directions_clicked", { source: "location" })}
                  className="btn-primary w-full justify-center"
                  data-testid="location-directions"
                >
                  <Navigation className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Directions
                </a>
                <a
                  href={`tel:${config.phone_tel}`}
                  onClick={() => trackEvent("call_clicked", { source: "location" })}
                  className="btn-ghost w-full justify-center"
                  data-testid="location-call"
                >
                  <Phone className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Call
                </a>
              </div>
            </div>

            <div className="mt-4 border border-white/10 p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="w-5 h-5" strokeWidth={1.4} />
                <div className="eyebrow">Hours</div>
              </div>
              <ul className="divide-y divide-white/10">
                {hoursOrder.map((dIdx) => {
                  const h = config.hours.find(
                    (x, i) =>
                      i ===
                      config.hours.findIndex((hh) =>
                        hh.day.toLowerCase().startsWith(
                          ["sun", "mon", "tue", "wed", "thu", "fri", "sat"][dIdx]
                        )
                      )
                  );
                  if (!h) return null;
                  const isToday = today === dIdx;
                  return (
                    <li
                      key={h.day}
                      className={`flex items-center justify-between py-3 ${
                        isToday ? "text-white" : "text-zinc-400"
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        {isToday && (
                          <span className="w-1.5 h-1.5 rounded-full bg-white" />
                        )}
                        <span className="font-serif text-base">
                          {h.day}
                        </span>
                      </span>
                      <span className="font-mono text-xs">
                        {h.closed ? "Closed" : `${h.open} — ${h.close}`}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="mt-4 border border-white/10 p-6 md:p-8">
              <div className="eyebrow mb-3">Contact</div>
              <div className="space-y-3">
                <a
                  href={`tel:${config.phone_tel}`}
                  className="flex items-center gap-3 group"
                >
                  <Phone className="w-4 h-4 text-zinc-500 group-hover:text-white transition-colors" strokeWidth={1.4} />
                  <span className="font-serif text-lg line-hover">{config.phone}</span>
                </a>
                <a
                  href={`mailto:${config.email}`}
                  className="flex items-center gap-3 group"
                  onClick={() => trackEvent("email_clicked", {})}
                  data-testid="location-email"
                >
                  <Mail className="w-4 h-4 text-zinc-500 group-hover:text-white transition-colors" strokeWidth={1.4} />
                  <span className="font-serif text-lg line-hover">{config.email}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
